import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  TextField,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  ArrowForward as NextIcon,
  ArrowBack as PrevIcon,
} from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import { facilityService } from "../../services/facilityService";
import { useAuth } from "../../contexts/AuthContext";

const CreateFacility = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  // Form data state
  const [formData, setFormData] = useState({
    company_name: "",
    phone_number: "",
    address: "",
    email: "",
    fax: "",
    business_license: "",
    npi: "",
    credit_card_last4: "",
    billing_address: "",
    subscription_type: "basic",
    admin_level: 1,
  });

  // Stepper steps
  const steps = [
    "Company Information",
    "Licensing & Billing",
    "Subscription Settings",
  ];

  // Subscription types and admin levels
  const subscriptionTypes = [
    {
      value: "basic",
      label: "Basic",
      description: "Essential features for small facilities",
    },
    {
      value: "standard",
      label: "Standard",
      description: "Enhanced features for medium facilities",
    },
    {
      value: "premium",
      label: "Premium",
      description: "Full feature set for large facilities",
    },
  ];

  const adminLevels = [
    { value: 1, label: "Basic", description: "Limited administrative access" },
    {
      value: 2,
      label: "Standard",
      description: "Standard administrative access",
    },
    { value: 3, label: "Advanced", description: "Full administrative access" },
    {
      value: 4,
      label: "Super Admin",
      description: "System-wide administrative access",
    },
  ];

  // Check permissions
  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          You don't have permission to create facilities. Only super admins can
          create new facilities.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/facilities")}
        >
          Back to Facilities
        </Button>
      </Box>
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateCurrentStep = () => {
    const errors = {};

    if (activeStep === 0) {
      // Company Information validation
      if (!formData.company_name?.trim()) {
        errors.company_name = "Company name is required";
      }
      if (!formData.email?.trim()) {
        errors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = "Invalid email format";
      }
      if (!formData.phone_number?.trim()) {
        errors.phone_number = "Phone number is required";
      }
      if (!formData.address?.trim()) {
        errors.address = "Address is required";
      }
    } else if (activeStep === 2) {
      // Subscription settings validation
      if (!formData.subscription_type) {
        errors.subscription_type = "Subscription type is required";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      setError("Please fix the validation errors before proceeding");
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    // Final validation
    const validation = facilityService.validateFacilityData(formData);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setSaving(false);
      setError("Please fix all validation errors before submitting");
      return;
    }

    try {
      const response = await facilityService.createFacility(formData);
      setSuccess("Facility created successfully!");

      // Redirect to facility detail page
      setTimeout(() => {
        navigate(`/facilities/${response.id}`);
      }, 1500);
    } catch (err) {
      console.error("Error creating facility:", err);
      setError(
        err.message || err.response?.data?.detail || "Failed to create facility"
      );
    } finally {
      setSaving(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.company_name}
                helperText={fieldErrors.company_name}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.phone_number}
                helperText={fieldErrors.phone_number}
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Fax Number"
                name="fax"
                value={formData.fax}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                multiline
                rows={2}
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Business License"
                name="business_license"
                value={formData.business_license}
                onChange={handleInputChange}
                helperText="Optional - Enter business license number"
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="NPI Number"
                name="npi"
                value={formData.npi}
                onChange={handleInputChange}
                helperText="Optional - National Provider Identifier"
              />
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Credit Card Last 4 Digits"
                name="credit_card_last4"
                value={formData.credit_card_last4}
                onChange={handleInputChange}
                inputProps={{ maxLength: 4 }}
                helperText="Optional - For billing purposes"
              />
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Billing Address"
                name="billing_address"
                value={formData.billing_address}
                onChange={handleInputChange}
                multiline
                rows={2}
                helperText="Optional - Leave empty to use company address"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid  size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Subscription Type
              </Typography>
              <Grid container spacing={2}>
                {subscriptionTypes.map((type) => (
                  <Grid key={type.value} size={{ xs: 12, sm: 4 }}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        border:
                          formData.subscription_type === type.value ? 2 : 1,
                        borderColor:
                          formData.subscription_type === type.value
                            ? "primary.main"
                            : "grey.300",
                      }}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          subscription_type: type.value,
                        }))
                      }
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {type.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {type.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Admin Level</InputLabel>
                <Select
                  name="admin_level"
                  value={formData.admin_level}
                  onChange={handleInputChange}
                  label="Admin Level"
                >
                  {adminLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      <Box>
                        <Typography variant="body1">{level.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {level.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      default:
        return "Unknown step";
    }
  };

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            component={Link}
            to="/facilities"
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Facilities
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Create New Facility
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            {steps[activeStep]}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {getStepContent(activeStep)}

          {/* Navigation Buttons */}
          <Box
            display="flex"
            justifyContent="space-between"
            mt={4}
            pt={3}
            borderTop={1}
            borderColor="divider"
          >
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<PrevIcon />}
            >
              Back
            </Button>

            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                >
                  {saving ? "Creating..." : "Create Facility"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<NextIcon />}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </form>

      {/* Preview Card */}
      {activeStep === steps.length - 1 && (
        <Paper elevation={3} sx={{ p: 4, mt: 3, borderRadius: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Facility Preview
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Company Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.company_name || "N/A"}
              </Typography>
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.email || "N/A"}
              </Typography>
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.phone_number || "N/A"}
              </Typography>
            </Grid>
            <Grid  size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Subscription
              </Typography>
              <Typography variant="body1" gutterBottom>
                {subscriptionTypes.find(
                  (t) => t.value === formData.subscription_type
                )?.label || "N/A"}
              </Typography>
            </Grid>
            <Grid  size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Address
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.address || "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default CreateFacility;
